import { Sparkles } from 'lucide-react'
import { Tooltip } from '@/components/ds2'

interface FillWithAiButtonProps {
    /** The card's name, for the button's accessible name. */
    cardLabel: string
    /** The fields AI can fill from this card, by their form labels: HR sees them before pressing. */
    fields: readonly string[]
    /** Why it can't be pressed now, shown on hover; undefined when it can. */
    unavailable?: string
    onFill: () => void
}

/**
 * The AI button at the top of a document card the form reads: one press reads every file on the card not read yet. Nothing is read
 * until HR presses it. Unavailable, it stays focusable and hoverable (`aria-disabled`, not `disabled`) so the reason shows.
 */
export const FillWithAiButton = ({ cardLabel, fields, unavailable, onFill }: FillWithAiButtonProps) => (
    <Tooltip
        content={
            <span className="flex flex-col gap-0.5">
                <span className="font-medium">Fill with AI</span>
                <span>Fills: {fields.join(', ')}</span>
                <span>Empty fields only. Filled ones get a suggestion.</span>
                {unavailable && <span className="font-medium">{unavailable}</span>}
            </span>
        }
        placement="topEnd"
    >
        <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors aria-[disabled=false]:hover:bg-accent aria-[disabled=false]:hover:text-accent-foreground aria-disabled:cursor-not-allowed aria-disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label={`Fill fields from the ${cardLabel} files with AI`}
            aria-disabled={unavailable !== undefined}
            onClick={(event) => {
                event.stopPropagation()
                if (unavailable === undefined) onFill()
            }}
        >
            <Sparkles className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
    </Tooltip>
)
